import { SCREEN_RIGHT_WORLD_SIGN } from "../config/GameConfig";
export interface InputSource {
  readonly axes: Readonly<Record<string, number>>;
  readonly buttons: Readonly<Record<string, boolean>>;
  update(rawKeys?: Record<string, boolean>, prevRawKeys?: Record<string, boolean>): void;
  getAxis(axis: string): number;
  isPressed(button: string): boolean;
  justPressed(button: string): boolean;
  justReleased(button: string): boolean;
}

export class CompositeInputSource implements InputSource {
  constructor(private readonly sources: InputSource[]) {}

  get axes(): Readonly<Record<string, number>> {
    const merged: Record<string, number> = {};
    for (const source of this.sources) {
      for (const [axis, value] of Object.entries(source.axes)) {
        const current = merged[axis] ?? 0;
        merged[axis] = Math.abs(value) > Math.abs(current) ? value : current;
      }
    }
    return merged;
  }

  get buttons(): Readonly<Record<string, boolean>> {
    const merged: Record<string, boolean> = {};
    for (const source of this.sources) {
      for (const [button, value] of Object.entries(source.buttons)) {
        merged[button] = merged[button] || value;
      }
    }
    return merged;
  }

  update(): void {
    // Child sources are updated by their owners.
  }

  getAxis(axis: string): number {
    return this.axes[axis] ?? 0;
  }

  isPressed(button: string): boolean {
    return this.sources.some((source) => source.isPressed(button));
  }

  justPressed(button: string): boolean {
    return this.sources.some((source) => source.justPressed(button));
  }

  justReleased(button: string): boolean {
    return this.sources.some((source) => source.justReleased(button));
  }
}

export interface KeyboardInputConfig {
  axes?: Record<string, { negative?: string[]; positive?: string[] }>;
  buttons?: Record<string, string[]>;
}

export class KeyboardInput implements InputSource {
  private axisBindings: Record<string, { negative: string[]; positive: string[] }>;
  private buttonBindings: Record<string, string[]>;
  private axisState: Record<string, number> = {};
  private buttonState: Record<string, boolean> = {};
  private prevButtonState: Record<string, boolean> = {};

  constructor(config: KeyboardInputConfig) {
    this.axisBindings = Object.fromEntries(
      Object.entries(config.axes ?? {}).map(([axis, binding]) => [
        axis,
        {
          negative: binding.negative ?? [],
          positive: binding.positive ?? [],
        },
      ]),
    );
    this.buttonBindings = config.buttons ?? {};
  }

  get axes(): Readonly<Record<string, number>> {
    return this.axisState;
  }

  get buttons(): Readonly<Record<string, boolean>> {
    return this.buttonState;
  }

  update(rawKeys: Record<string, boolean> = {}): void {
    this.prevButtonState = { ...this.buttonState };

    for (const [axis, binding] of Object.entries(this.axisBindings)) {
      let value = 0;
      if (binding.negative.some((key) => !!rawKeys[key])) {
        value -= 1;
      }
      if (binding.positive.some((key) => !!rawKeys[key])) {
        value += 1;
      }
      this.axisState[axis] = Math.max(-1, Math.min(1, value));
    }

    for (const [button, keys] of Object.entries(this.buttonBindings)) {
      this.buttonState[button] = keys.some((key) => !!rawKeys[key]);
    }
  }

  getAxis(axis: string): number {
    return this.axisState[axis] ?? 0;
  }

  isPressed(button: string): boolean {
    return !!this.buttonState[button];
  }

  justPressed(button: string): boolean {
    return !!this.buttonState[button] && !this.prevButtonState[button];
  }

  justReleased(button: string): boolean {
    return !this.buttonState[button] && !!this.prevButtonState[button];
  }
}

export interface GamepadInputConfig {
  axes?: Record<string, { index: number; deadzone?: number; invert?: boolean }>;
  buttons?: Record<string, number[]>;
}

export class GamepadInput implements InputSource {
  private axisBindings: Record<string, { index: number; deadzone: number; invert: boolean }>;
  private buttonBindings: Record<string, number[]>;
  private axisState: Record<string, number> = {};
  private buttonState: Record<string, boolean> = {};
  private prevButtonState: Record<string, boolean> = {};

  constructor(
    private readonly gamepadIndex: number,
    config: GamepadInputConfig,
  ) {
    this.axisBindings = Object.fromEntries(
      Object.entries(config.axes ?? {}).map(([axis, binding]) => [
        axis,
        {
          index: binding.index,
          deadzone: binding.deadzone ?? 0.2,
          invert: binding.invert ?? false,
        },
      ]),
    );
    this.buttonBindings = config.buttons ?? {};
  }

  get axes(): Readonly<Record<string, number>> {
    return this.axisState;
  }

  get buttons(): Readonly<Record<string, boolean>> {
    return this.buttonState;
  }

  update(): void {
    this.prevButtonState = { ...this.buttonState };
    const pad = navigator.getGamepads?.()[this.gamepadIndex] ?? null;

    for (const [axis, binding] of Object.entries(this.axisBindings)) {
      const raw = pad?.axes?.[binding.index] ?? 0;
      const value = Math.abs(raw) < binding.deadzone ? 0 : (binding.invert ? -raw : raw);
      this.axisState[axis] = Math.max(-1, Math.min(1, value));
    }

    for (const [button, indices] of Object.entries(this.buttonBindings)) {
      this.buttonState[button] = indices.some((index) => !!pad?.buttons?.[index]?.pressed);
    }
  }

  getAxis(axis: string): number {
    return this.axisState[axis] ?? 0;
  }

  isPressed(button: string): boolean {
    return !!this.buttonState[button];
  }

  justPressed(button: string): boolean {
    return !!this.buttonState[button] && !this.prevButtonState[button];
  }

  justReleased(button: string): boolean {
    return !this.buttonState[button] && !!this.prevButtonState[button];
  }
}

type TouchButtonId =
  | "left"
  | "right"
  | "jump"
  | "switch"
  | "q"
  | "e"
  | "f"
  | "r"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "f+m"
  | "g+n";

export class TouchInput implements InputSource {
  private axisState: Record<string, number> = {};
  private buttonState: Record<string, boolean> = {};
  private prevButtonState: Record<string, boolean> = {};
  private container: HTMLDivElement | null = null;
  private buttonElements = new Map<TouchButtonId, HTMLButtonElement>();
  private currentButtons: TouchButtonId[] = [];
  private activePlayer: 1 | 2 = 1;
  // Default co-op (see InputManager.ts for the rationale).
  private soloMode = false;

  constructor() {
    if (typeof document === "undefined") {
      return;
    }
    this.buildOverlay();
  }

  get axes(): Readonly<Record<string, number>> {
    return this.axisState;
  }

  get buttons(): Readonly<Record<string, boolean>> {
    return this.buttonState;
  }

  update(): void {
    this.prevButtonState = { ...this.buttonState };
    // Touch "right" moves toward screen-right (see VIEW.MIRROR_X).
    this.axisState.moveX =
      SCREEN_RIGHT_WORLD_SIGN * ((this.buttonState.right ? 1 : 0) - (this.buttonState.left ? 1 : 0));
  }

  getAxis(axis: string): number {
    return this.axisState[axis] ?? 0;
  }

  isPressed(button: string): boolean {
    if (button === "jump") {
      return !!this.buttonState.jump;
    }
    return !!this.buttonState[button];
  }

  justPressed(button: string): boolean {
    return !!this.buttonState[button] && !this.prevButtonState[button];
  }

  justReleased(button: string): boolean {
    return !this.buttonState[button] && !!this.prevButtonState[button];
  }

  setContext(activePlayer: 1 | 2, soloMode: boolean): void {
    this.activePlayer = activePlayer;
    this.soloMode = soloMode;
    this.renderButtons();
    if (this.container) {
      this.container.style.display = soloMode ? "block" : "none";
    }
  }

  dispose(): void {
    this.container?.remove();
    this.container = null;
    this.buttonElements.clear();
  }

  private buildOverlay(): void {
    if (!("ontouchstart" in window || navigator.maxTouchPoints > 0)) {
      return;
    }

    const container = document.createElement("div");
    container.id = "touchControls";
    container.style.position = "fixed";
    container.style.inset = "0";
    container.style.pointerEvents = "none";
    container.style.zIndex = "30";

    const style = document.createElement("style");
    style.textContent = `
      #touchControls .touch-cluster {
        position: absolute;
        display: flex;
        gap: 10px;
        pointer-events: none;
      }
      #touchControls button {
        pointer-events: auto;
        min-width: 58px;
        min-height: 58px;
        border-radius: 18px;
        border: 1px solid rgba(196,155,60,0.75);
        background: rgba(16, 10, 28, 0.82);
        color: #f5eedb;
        font: 600 16px Georgia, serif;
        box-shadow: 0 10px 28px rgba(0,0,0,0.28);
        touch-action: manipulation;
      }
      #touchControls button.touch-hidden {
        display: none;
      }
    `;
    document.head.appendChild(style);

    const movement = document.createElement("div");
    movement.className = "touch-cluster";
    movement.style.left = "16px";
    movement.style.bottom = "18px";

    const primary = document.createElement("div");
    primary.className = "touch-cluster";
    primary.style.right = "16px";
    primary.style.bottom = "18px";
    primary.style.flexWrap = "wrap";
    primary.style.width = "210px";
    primary.style.justifyContent = "flex-end";

    for (const [id, label, host] of [
      ["left", "←", movement],
      ["right", "→", movement],
      ["jump", "Jump", primary],
      ["switch", "Swap", primary],
      ["q", "Aura", primary],
      ["e", "Slip", primary],
      ["f", "Purr", primary],
      ["r", "Latch", primary],
      ["1", "Turn", primary],
      ["2", "Hit", primary],
      ["3", "Anchor", primary],
      ["4", "Leap", primary],
      ["5", "Shock", primary],
      ["f+m", "Drop", primary],
      ["g+n", "Bubble", primary],
    ] as const) {
      const button = document.createElement("button");
      button.textContent = label;
      this.bindTouchButton(button, id);
      this.buttonElements.set(id, button);
      host.appendChild(button);
    }

    container.appendChild(movement);
    container.appendChild(primary);
    document.body.appendChild(container);
    this.container = container;
    this.renderButtons();
  }

  private renderButtons(): void {
    const visibleButtons: TouchButtonId[] = ["left", "right", "jump", "switch", "f+m", "g+n"];
    if (this.activePlayer === 1) {
      visibleButtons.push("q", "e", "f", "r");
    } else {
      visibleButtons.push("1", "2", "3", "4", "5");
    }

    this.currentButtons = visibleButtons;
    for (const [id, element] of this.buttonElements.entries()) {
      element.classList.toggle("touch-hidden", !visibleButtons.includes(id));
    }
  }

  private bindTouchButton(button: HTMLButtonElement, id: TouchButtonId): void {
    const press = (event: Event) => {
      event.preventDefault();
      if (id === "f+m") {
        this.buttonState.f = true;
        this.buttonState.m = true;
      } else if (id === "g+n") {
        this.buttonState.g = true;
        this.buttonState.n = true;
      } else {
        this.buttonState[id] = true;
      }
    };

    const release = (event: Event) => {
      event.preventDefault();
      if (id === "f+m") {
        this.buttonState.f = false;
        this.buttonState.m = false;
      } else if (id === "g+n") {
        this.buttonState.g = false;
        this.buttonState.n = false;
      } else {
        this.buttonState[id] = false;
      }
    };

    button.addEventListener("pointerdown", press);
    button.addEventListener("pointerup", release);
    button.addEventListener("pointercancel", release);
    button.addEventListener("pointerleave", release);
  }
}

export class NullInputSource implements InputSource {
  readonly axes: Readonly<Record<string, number>> = Object.freeze({});
  readonly buttons: Readonly<Record<string, boolean>> = Object.freeze({});

  update(): void {}

  getAxis(_axis: string): number {
    return 0;
  }

  isPressed(_button: string): boolean {
    return false;
  }

  justPressed(_button: string): boolean {
    return false;
  }

  justReleased(_button: string): boolean {
    return false;
  }
}
